import React, { useEffect, useMemo, useState } from "react";
import "./AIUsageDashboard.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`;

const num = (v) => Number(v || 0).toLocaleString();

const pct = (part, total) => {
  if (!total) return 0;
  return Math.min(100, Math.round((Number(part || 0) / Number(total || 0)) * 100));
};

const safe = (v) => v || "Unknown";

export default function AIUsageDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState("all");
  const [modelFilter, setModelFilter] = useState("all");
  const [featureFilter, setFeatureFilter] = useState("all");
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const loadUsage = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `${API_BASE}/search/ai_usage_logs/?limit=5000&sort_by=created_at&sort_order=desc`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!res.ok) throw new Error(`AI usage request failed: ${res.status}`);

        const data = await res.json();
        setRows(data.records || []);
      } catch (e) {
        setError(e.message || "Failed to load AI usage.");
      } finally {
        setLoading(false);
      }
    };

    loadUsage();
  }, [token]);

  const tenants = useMemo(
    () => [...new Set(rows.map((r) => safe(r.sip_tenant_name)))],
    [rows]
  );

  const models = useMemo(
    () => [...new Set(rows.map((r) => safe(r.sip_model)))],
    [rows]
  );

  const features = useMemo(
    () => [...new Set(rows.map((r) => safe(r.sip_feature)))],
    [rows]
  );

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const tenantOk = tenantFilter === "all" || safe(r.sip_tenant_name) === tenantFilter;
      const modelOk = modelFilter === "all" || safe(r.sip_model) === modelFilter;
      const featureOk = featureFilter === "all" || safe(r.sip_feature) === featureFilter;
      return tenantOk && modelOk && featureOk;
    });
  }, [rows, tenantFilter, modelFilter, featureFilter]);

  const stats = useMemo(() => {
    const totalCost = filtered.reduce((s, r) => s + Number(r.sip_estimated_cost_usd || 0), 0);
    const totalTokens = filtered.reduce((s, r) => s + Number(r.sip_total_tokens || 0), 0);
    const inputTokens = filtered.reduce((s, r) => s + Number(r.sip_input_tokens || 0), 0);
    const outputTokens = filtered.reduce((s, r) => s + Number(r.sip_output_tokens || 0), 0);
    const totalDuration = filtered.reduce((s, r) => s + Number(r.sip_duration_ms || 0), 0);
    // Agent runs only ever log "completed" or "needs_approval" (see
    // ai_agents/runtime.py::_log_usage) -- there's no "success" status and,
    // right now, no logged "failed" status either (a hard failure that
    // throws before _log_usage runs isn't recorded at all yet). Matching
    // against the literal string "success" here matched nothing, so every
    // row fell through to "failed" -- a 100%-failure dashboard for a fleet
    // that's never actually had a failed call logged.
    const success = filtered.filter((r) => String(r.sip_status || "").toLowerCase() === "completed").length;
    const needsApproval = filtered.filter((r) => String(r.sip_status || "").toLowerCase() === "needs_approval").length;
    const failed = filtered.length - success - needsApproval;

    return {
      totalCost,
      totalTokens,
      inputTokens,
      outputTokens,
      requests: filtered.length,
      success,
      needsApproval,
      failed,
      successRate: filtered.length ? Math.round((success / filtered.length) * 100) : 0,
      avgCost: filtered.length ? totalCost / filtered.length : 0,
      avgTokens: filtered.length ? totalTokens / filtered.length : 0,
      avgDuration: filtered.length ? totalDuration / filtered.length : 0,
    };
  }, [filtered]);

  const groupBy = (field) => {
    const map = {};
    filtered.forEach((r) => {
      const key = safe(r[field]);
      map[key] ||= { name: key, cost: 0, tokens: 0, requests: 0, duration: 0 };
      map[key].cost += Number(r.sip_estimated_cost_usd || 0);
      map[key].tokens += Number(r.sip_total_tokens || 0);
      map[key].duration += Number(r.sip_duration_ms || 0);
      map[key].requests += 1;
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  };

  const byTenant = useMemo(() => groupBy("sip_tenant_name"), [filtered]);
  const byFeature = useMemo(() => groupBy("sip_feature"), [filtered]);
  const byModel = useMemo(() => groupBy("sip_model"), [filtered]);
  const byProvider = useMemo(() => groupBy("sip_provider"), [filtered]);

  const costByDay = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const d = r.created_at || r.sip_started_at;
      const key = d ? new Date(d).toLocaleDateString() : "Unknown";
      map[key] ||= { name: key, cost: 0, requests: 0, tokens: 0 };
      map[key].cost += Number(r.sip_estimated_cost_usd || 0);
      map[key].requests += 1;
      map[key].tokens += Number(r.sip_total_tokens || 0);
    });
    return Object.values(map).slice(0, 10).reverse();
  }, [filtered]);

  const mostExpensive = useMemo(
    () =>
      [...filtered]
        .sort((a, b) => Number(b.sip_estimated_cost_usd || 0) - Number(a.sip_estimated_cost_usd || 0))
        .slice(0, 10),
    [filtered]
  );

  const slowest = useMemo(
    () =>
      [...filtered]
        .sort((a, b) => Number(b.sip_duration_ms || 0) - Number(a.sip_duration_ms || 0))
        .slice(0, 10),
    [filtered]
  );

  if (loading) {
    return (
      <div className="aiu-page">
        <div className="aiu-state">Loading AI usage analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aiu-page">
        <div className="aiu-state aiu-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="aiu-page">
      <header className="aiu-hero">
        <div>
          <div className="aiu-eyebrow">AI Cost Center</div>
          <h1>AI Usage Analytics</h1>
          <p>Real Claude (Anthropic) and HERE Maps spend, call volume, expensive calls, failures, and performance.</p>
        </div>

        <div className="aiu-hero-badge">
          <span>Success Rate</span>
          <strong>{stats.successRate}%</strong>
        </div>
      </header>

      <section className="aiu-filters-card">
        <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}>
          <option value="all">All tenants</option>
          {tenants.map((t) => <option key={t}>{t}</option>)}
        </select>

        <select value={modelFilter} onChange={(e) => setModelFilter(e.target.value)}>
          <option value="all">All models</option>
          {models.map((m) => <option key={m}>{m}</option>)}
        </select>

        <select value={featureFilter} onChange={(e) => setFeatureFilter(e.target.value)}>
          <option value="all">All features</option>
          {features.map((f) => <option key={f}>{f}</option>)}
        </select>
      </section>

      <section className="aiu-cards">
        <Metric tone="purple" title="Total Cost" value={money(stats.totalCost)} sub={`${num(stats.requests)} AI calls`} />
        <Metric tone="blue" title="Total Tokens" value={num(stats.totalTokens)} sub={`${num(stats.inputTokens)} in / ${num(stats.outputTokens)} out`} />
        <Metric tone="green" title="Success Rate" value={`${stats.successRate}%`} sub={`${num(stats.success)} success / ${num(stats.needsApproval)} pending approval / ${num(stats.failed)} failed`} />
        <Metric tone="orange" title="Avg Duration" value={`${Math.round(stats.avgDuration / 1000)}s`} sub={`${Math.round(stats.avgTokens)} avg tokens`} />
      </section>

      <section className="aiu-grid">
        <Panel title="Cost Trend" subtitle="Daily AI spend for the selected filters">
          <MiniTrend items={costByDay} />
        </Panel>

        <Panel title="Cost by Tenant" subtitle="Which customers consume the most AI budget">
          <BarList items={byTenant} total={stats.totalCost} valueKey="cost" valueFormat={money} />
        </Panel>

        <Panel title="Cost by Feature" subtitle="Where your AI spend is going">
          <BarList items={byFeature} total={stats.totalCost} valueKey="cost" valueFormat={money} />
        </Panel>

        <Panel title="Model Usage" subtitle="Compare spend and tokens by model">
          <BarList items={byModel} total={stats.totalCost} valueKey="cost" valueFormat={money} />
        </Panel>

        <Panel title="Provider Usage" subtitle="Spend split by AI provider">
          <BarList items={byProvider} total={stats.totalCost} valueKey="cost" valueFormat={money} />
        </Panel>

        <Panel title="Most Expensive AI Calls" subtitle="Top calls by estimated cost">
          <UsageTable rows={mostExpensive} mode="cost" />
        </Panel>

        <Panel title="Slowest AI Calls" subtitle="Top calls by response duration">
          <UsageTable rows={slowest} mode="duration" />
        </Panel>
      </section>
    </div>
  );
}

function Metric({ title, value, sub, tone }) {
  return (
    <div className={`aiu-card aiu-card-${tone}`}>
      <span>{title}</span>
      <strong>{value}</strong>
      <p>{sub}</p>
    </div>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <div className="aiu-panel">
      <div className="aiu-panel-head">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function BarList({ items, total, valueKey, valueFormat }) {
  if (!items.length) return <div className="aiu-empty">No data found.</div>;

  return (
    <div className="aiu-bars">
      {items.slice(0, 8).map((item) => (
        <div className="aiu-bar-row" key={item.name}>
          <div className="aiu-bar-top">
            <strong>{item.name}</strong>
            <span>{valueFormat(item[valueKey])}</span>
          </div>
          <div className="aiu-bar-track">
            <div style={{ width: `${pct(item[valueKey], total)}%` }} />
          </div>
          <div className="aiu-bar-meta">
            {num(item.requests)} requests · {num(item.tokens)} tokens · avg {Math.round(item.duration / item.requests / 1000 || 0)}s
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniTrend({ items }) {
  const max = Math.max(...items.map((i) => i.cost), 0);

  if (!items.length) return <div className="aiu-empty">No trend data found.</div>;

  return (
    <div className="aiu-trend">
      {items.map((i) => (
        <div className="aiu-trend-col" key={i.name}>
          <div className="aiu-trend-bar" style={{ height: `${Math.max(8, pct(i.cost, max))}%` }} />
          <span>{i.name}</span>
          <strong>{money(i.cost)}</strong>
        </div>
      ))}
    </div>
  );
}

function UsageTable({ rows, mode }) {
  return (
    <table className="aiu-table">
      <thead>
        <tr>
          <th>Tenant</th>
          <th>Feature</th>
          <th>Model</th>
          <th>Tokens</th>
          <th>{mode === "duration" ? "Duration" : "Cost"}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{r.sip_tenant_name || "Unknown"}</td>
            <td>{r.sip_feature || "Unknown"}</td>
            <td>{r.sip_model || "Unknown"}</td>
            <td>{num(r.sip_total_tokens)}</td>
            <td>
              {mode === "duration"
                ? `${Math.round(Number(r.sip_duration_ms || 0) / 1000)}s`
                : money(r.sip_estimated_cost_usd)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}